/**
 * Constantes définissant les origines possibles des candidatures
 *
 * Cette énumération contient tous les canaux par lesquels un candidat peut
 * postuler à une offre d'emploi dans le système.
 *
 * @readonly
 * @enum {string}
 */
export const CANDIDATE_APPLICATION_ORIGIN = {
  /** Candidature provenant de France Travail (Pôle Emploi) */
  FRANCE_TRAVAIL: 'france-travail',
  /** Candidature provenant de la plateforme Indeed */
  INDEED: 'indeed',
  /** Candidature provenant de LinkedIn */
  LINKEDIN: 'linkedin',
  /** Candidature provenant directement du site carrière de l'entreprise */
  CAREER_SITE: 'career-site',
  LAMACOMPTA: 'lamacompta',
} as const

/**
 * Tableau contenant toutes les valeurs possibles des origines de candidature
 *
 * Cette constante est générée automatiquement à partir des valeurs de
 * {@link CANDIDATE_APPLICATION_ORIGIN} et peut être utilisée pour la validation
 * ou l'affichage de toutes les options disponibles.
 *
 * @readonly
 * @type {readonly string[]}
 *
 * @example
 * ```typescript
 * // Validation d'une origine de candidature
 * function isValidOrigin(origin: string): boolean {
 *   return CANDIDATE_APPLICATION_ORIGIN_VALUES.includes(origin)
 * }
 *
 * // Affichage des options dans un formulaire
 * const originOptions = CANDIDATE_APPLICATION_ORIGIN_VALUES.map(origin => ({
 *   value: origin,
 *   label: origin.charAt(0).toUpperCase() + origin.slice(1)
 * }))
 * ```
 */
export const CANDIDATE_APPLICATION_ORIGIN_VALUES = Object.values(CANDIDATE_APPLICATION_ORIGIN)

export function getOrigin({
  isCareerSite,
  searchParams,
}: {
  isCareerSite: boolean
  searchParams: URLSearchParams
}) {
  if (!searchParams) {
    return null
  }

  const origin = searchParams.get('origin')

  if (origin && origin === CANDIDATE_APPLICATION_ORIGIN.INDEED) {
    return CANDIDATE_APPLICATION_ORIGIN.INDEED
  }

  if (isCareerSite) {
    return CANDIDATE_APPLICATION_ORIGIN.CAREER_SITE
  }

  if (origin) {
    return origin
  }

  return CANDIDATE_APPLICATION_ORIGIN.LAMACOMPTA
}
